import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check, MessageCircle, CalendarDays } from "lucide-react";
import Reveal from "@/components/Reveal";
import GoldSeam from "@/components/GoldSeam";
import { TestimonialCard } from "@/components/TestimonialCard";
import { SERVICES, FEES, STEPS_TO_BOOK } from "@/data/services";
import { TESTIMONIALS } from "@/data/testimonials";
import { waLink, WHATSAPP_DISPLAY } from "@/lib/site";

const FEES_TESTIMONIALS = TESTIMONIALS.filter((t) => ["jbp-1", "hyd-1"].includes(t.id));

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <main className="pt-20" data-testid="services-page">
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div
          className="absolute -top-32 right-0 w-[480px] h-[480px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(169,129,47,0.13), transparent 62%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="eyebrow mb-6">Services</p>
            <h1
              className="font-display text-ink font-medium leading-[1.06] max-w-4xl"
              style={{ fontSize: "clamp(2.4rem, 5.4vw, 4.4rem)" }}
              data-testid="services-heading"
            >
              Five rooms. One practice.
            </h1>
            <p className="mt-8 text-inkdim leading-relaxed max-w-2xl text-[15px] md:text-base">
              Private conversations, corporate programmes, guided practice, circles and
              offline gatherings. Every format runs on the same sequence — see the pattern,
              understand it, release it, and realign around what's actually true.
            </p>
            <GoldSeam variant="horizontal" className="mt-10 w-64 h-10 opacity-70" delay={0.2} />
          </Reveal>
        </div>
      </section>

      {SERVICES.map((s, i) => (
        <section
          key={s.slug}
          id={s.slug}
          className={`py-16 md:py-24 border-t border-[rgba(169,129,47,0.26)] ${i % 2 ? "bg-paper" : ""}`}
          data-testid={`service-block-${s.slug}`}
        >
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <Reveal className={i % 2 ? "lg:order-2" : ""}>
              <div className="relative border border-[rgba(169,129,47,0.26)] p-3">
                <span className="absolute -top-px -left-px w-5 h-5 border-t border-l border-gold" aria-hidden="true" />
                <span className="absolute -bottom-px -right-px w-5 h-5 border-b border-r border-gold" aria-hidden="true" />
                <div className="overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-[280px] md:h-[380px] object-cover hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                    data-testid={`service-image-${s.slug}`}
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <span className="text-[11px] tracking-[0.3em] text-gold">{s.n}</span>
              <h2 className="mt-5 font-display text-ink font-medium leading-[1.12] text-3xl md:text-4xl">
                {s.name}
              </h2>
              <p className="mt-4 font-display italic text-goldink text-xl md:text-2xl leading-snug">
                {s.tagline}
              </p>
              <p className="mt-6 text-inkdim leading-relaxed text-[15px] max-w-xl">{s.body}</p>
              <ul className="mt-8 space-y-3">
                {s.includes.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-ink">
                    <Check size={15} strokeWidth={1.6} className="text-gold mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-wrap items-center gap-6">
                <Link
                  to={`/contact?offering=${s.slug}`}
                  data-testid={`service-enquire-${s.slug}`}
                  className="bg-deep text-canvas text-[11px] uppercase tracking-[0.24em] font-semibold px-8 py-3.5 hover:bg-gold transition-colors duration-300"
                >
                  Enquire
                </Link>
                <a
                  href={waLink(`Hello Rajdeep, I'd like to know more about ${s.name}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-whatsapp-${s.slug}`}
                  className="gold-underline-link inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold"
                >
                  <MessageCircle size={14} strokeWidth={1.6} /> WhatsApp
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      ))}

      <section id="fees" className="py-20 md:py-32 border-t border-[rgba(169,129,47,0.26)] bg-sand/40" data-testid="fees-section">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow mb-6">Session fees</p>
            <h2 className="font-display text-ink font-medium leading-[1.1] text-4xl sm:text-5xl" data-testid="fees-heading">
              What it costs
            </h2>
            <p className="mt-6 text-inkdim leading-relaxed max-w-2xl text-[15px]">
              A private session is ₹2,100 for sixty minutes. Most people find the shift
              lands over three to four conversations — the first makes the pattern visible,
              the ones after make the new choice repeatable.
            </p>
          </Reveal>

          <div className="mt-12 border-t border-[rgba(169,129,47,0.26)]">
            {FEES.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.04}>
                <div
                  className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 py-6 border-b border-[rgba(169,129,47,0.26)]"
                  data-testid={`fee-row-${i}`}
                >
                  <div>
                    <p className="font-display text-xl md:text-2xl text-ink">{f.label}</p>
                    <p className="mt-1 text-xs text-inkdim">{f.note}</p>
                  </div>
                  <p className="font-display text-2xl text-gold shrink-0" data-testid={`fee-price-${i}`}>
                    {f.price}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap items-center gap-6">
              <Link
                to="/booking"
                data-testid="fees-book-session-button"
                className="inline-flex items-center gap-2 bg-deep text-canvas text-xs uppercase tracking-[0.24em] font-semibold px-9 py-4 hover:bg-gold transition-colors duration-300"
              >
                <CalendarDays size={15} strokeWidth={1.6} /> Book &amp; pay online
              </Link>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fees-whatsapp-link"
                className="gold-underline-link inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold"
              >
                <MessageCircle size={14} strokeWidth={1.6} /> {WHATSAPP_DISPLAY}
              </a>
              <Link
                to="/faq"
                data-testid="fees-terms-link"
                className="gold-underline-link text-[11px] uppercase tracking-[0.24em] text-inkdim hover:text-gold"
              >
                Important terms
              </Link>
            </div>
          </Reveal>
          </div>

          <aside className="lg:col-span-5 lg:sticky lg:top-28" data-testid="fees-testimonials">
            <Reveal>
              <p className="eyebrow mb-6">Before you decide</p>
              <div className="space-y-5">
                {FEES_TESTIMONIALS.map((t) => (
                  <TestimonialCard key={t.id} t={t} />
                ))}
              </div>
              <Link
                to="/#testimonials"
                data-testid="fees-all-testimonials-link"
                className="gold-underline-link inline-block mt-7 text-[11px] uppercase tracking-[0.24em] text-gold"
              >
                Read more client words
              </Link>
            </Reveal>
          </aside>
        </div>
      </section>

      <section className="py-20 md:py-32" data-testid="how-to-book-section">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="eyebrow mb-6">How to begin</p>
            <h2 className="font-display text-ink font-medium leading-[1.1] text-4xl sm:text-5xl" data-testid="how-to-book-heading">
              Three steps, no forms to fill in triplicate
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {STEPS_TO_BOOK.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="border-t border-gold pt-7" data-testid={`book-step-${s.n}`}>
                  <p className="font-display text-5xl text-gold">{s.n}</p>
                  <h3 className="mt-5 font-display text-2xl text-ink leading-snug">{s.title}</h3>
                  <p className="mt-3 text-inkdim text-sm leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
